import { Calculator, Sparkles, AlertTriangle, ShoppingBag, Landmark, Truck, Undo2, Lightbulb, Divide } from 'lucide-react';
import AvisoSugestaoIA from './AvisoSugestaoIA';
import { formatarMoeda, REGIMES } from './formato';

// Etapas do passo a passo, na ordem em que a conta é feita. Cores reaproveitadas na barra de composição.
const ETAPAS = [
  { grupo: 'produto', rotulo: 'Produto', titulo: 'O que a loja pagou ao fornecedor', icone: ShoppingBag, cor: 'bg-blue-500', texto: 'text-blue-700' },
  { grupo: 'impostos', rotulo: 'Impostos', titulo: 'Impostos que a loja paga e não recupera', icone: Landmark, cor: 'bg-rose-500', texto: 'text-rose-700' },
  { grupo: 'frete', rotulo: 'Frete', titulo: 'Frete pago à transportadora', icone: Truck, cor: 'bg-amber-500', texto: 'text-amber-700' },
  { grupo: 'creditos', rotulo: 'Volta para a loja', titulo: 'O que volta para a loja depois', icone: Undo2, cor: 'bg-emerald-500', texto: 'text-emerald-700' },
];

const sinal = (l) => (l.operacao === '-' ? -l.valor : l.valor);

function somaGrupo(memoria, grupo) {
  return memoria.filter(l => l.grupo === grupo && l.operacao !== 'info').reduce((s, l) => s + sinal(l), 0);
}

/** Barra horizontal mostrando de que é feito o custo (produto, impostos, frete), com o que volta à parte. */
function BarraComposicao({ memoria }) {
  const partes = ETAPAS.filter(e => e.grupo !== 'creditos')
    .map(e => ({ ...e, valor: somaGrupo(memoria, e.grupo) }))
    .filter(p => p.valor > 0);
  const total = partes.reduce((s, p) => s + p.valor, 0);
  const creditos = -somaGrupo(memoria, 'creditos');
  if (total <= 0) return null;

  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden bg-gray-100" aria-hidden="true">
        {partes.map(p => <div key={p.grupo} className={p.cor} style={{ width: `${(p.valor / total) * 100}%` }} />)}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
        {partes.map(p => (
          <span key={p.grupo} className="flex items-center gap-1">
            <span className={`w-2.5 h-2.5 rounded-full ${p.cor}`} />
            {p.rotulo}:
            {' '}<strong>{formatarMoeda(p.valor)}</strong> ({Math.round((p.valor / total) * 100)}%)
          </span>
        ))}
        {creditos > 0 && (
          <span className="flex items-center gap-1 text-emerald-700">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            Volta para a loja: <strong>− {formatarMoeda(creditos)}</strong>
          </span>
        )}
      </div>
    </div>
  );
}

function LinhaCalculo({ linha }) {
  const ehDesconto = linha.operacao === '-';
  return (
    <li className="flex items-start gap-3 py-2">
      <span className={`mt-0.5 w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${ehDesconto ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
        {ehDesconto ? '−' : '+'}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900">{linha.descricao}</p>
        {linha.ajuda && <p className="text-xs text-gray-500 leading-snug">{linha.ajuda}</p>}
      </div>
      <span className={`text-sm font-mono whitespace-nowrap ${ehDesconto ? 'text-emerald-700' : 'text-gray-900'}`}>
        {ehDesconto ? '− ' : ''}{formatarMoeda(linha.valor)}
      </span>
    </li>
  );
}

function PassoAPasso({ memoria }) {
  // Registros antigos do histórico não têm "grupo": mostra a lista simples.
  const temGrupos = memoria.some(l => l.grupo);
  if (!temGrupos) {
    return <ul className="divide-y divide-gray-100">{memoria.filter(l => l.operacao !== 'info').map((l, i) => <LinhaCalculo key={i} linha={l} />)}</ul>;
  }

  const etapas = ETAPAS.map(e => ({ ...e, linhas: memoria.filter(l => l.grupo === e.grupo && l.operacao !== 'info') }))
    .filter(e => e.linhas.length > 0);

  return (
    <ol className="space-y-3">
      {etapas.map((e, i) => {
        const Icone = e.icone;
        return (
          <li key={e.grupo} className="rounded-lg border border-gray-200">
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-t-lg">
              <p className={`flex items-center gap-2 text-sm font-semibold ${e.texto}`}>
                <span className="w-5 h-5 rounded-full bg-white border flex items-center justify-center text-[11px] text-gray-600">{i + 1}</span>
                <Icone className="w-4 h-4" /> {e.titulo}
              </p>
              <span className={`text-sm font-mono font-semibold ${e.texto}`}>
                {e.grupo === 'creditos' ? '− ' : ''}{formatarMoeda(Math.abs(somaGrupo(memoria, e.grupo)))}
              </span>
            </div>
            <ul className="divide-y divide-gray-100 px-3">{e.linhas.map((l, j) => <LinhaCalculo key={j} linha={l} />)}</ul>
          </li>
        );
      })}
    </ol>
  );
}

function BomSaber({ memoria }) {
  const infos = memoria.filter(l => l.operacao === 'info');
  if (infos.length === 0) return null;
  return (
    <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2">
      <p className="flex items-center gap-1 text-xs font-semibold text-sky-800 mb-1"><Lightbulb className="w-3.5 h-3.5" /> Bom saber (não muda o custo)</p>
      <ul className="space-y-1">
        {infos.map((l, i) => (
          <li key={i} className="text-xs text-sky-900">
            <strong>{l.descricao}: {formatarMoeda(l.valor)}.</strong> {l.ajuda}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CartaoItem({ r, item, explicacao }) {
  const fator = Number(item.fatorConversao) || 1;
  const pecas = r.unidadesVenda || Number(item.quantidade) * fator;
  const nomeUnidade = fator !== 1 ? 'peça' : (item.unidade || 'unidade').toLowerCase();
  const pagoFornecedor = Number(item.vProd || 0) - Number(item.vDesc || 0);
  const por100 = pagoFornecedor > 0 ? (r.custoTotal / pagoFornecedor) * 100 : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-gradient-to-r from-blue-50 to-white border-b border-gray-200">
        <div className="min-w-0">
          <p className="text-xs text-gray-500">Item {r.indice + 1} · {item.codigo}{item.ean ? ` · EAN ${item.ean}` : ''}</p>
          <p className="font-semibold text-gray-900 truncate">{item.descricao}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">Custo de cada {nomeUnidade}</p>
          <p className="text-3xl font-bold text-blue-700 leading-tight">{formatarMoeda(r.custoUnitario)}</p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-sm text-gray-700">
          A loja pagou <strong>{formatarMoeda(pagoFornecedor)}</strong> ao fornecedor por <strong>{pecas.toLocaleString('pt-BR')} {nomeUnidade}(s)</strong>.
          {' '}Somando impostos e frete, e tirando o que volta para a loja, o custo real ficou em <strong>{formatarMoeda(r.custoTotal)}</strong>.
          {por100 != null && (
            <> Ou seja: <strong>para cada R$ 100,00 da nota, o custo real é {formatarMoeda(por100)}</strong>.</>
          )}
        </p>

        <BarraComposicao memoria={r.memoria} />

        {explicacao && (
          <div className="flex items-start gap-2 text-sm text-gray-800 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
            <Sparkles className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
            <p><strong>Em poucas palavras:</strong> {explicacao}</p>
          </div>
        )}

        <details className="group" open>
          <summary className="cursor-pointer text-sm font-semibold text-gray-800 select-none">Ver a conta passo a passo</summary>
          <div className="mt-3 space-y-3">
            <PassoAPasso memoria={r.memoria} />

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-blue-600 text-white px-4 py-3">
              <span className="flex items-center gap-2 text-sm">
                <Divide className="w-4 h-4" />
                Custo total {formatarMoeda(r.custoTotal)} ÷ {pecas.toLocaleString('pt-BR')} {nomeUnidade}(s)
                {fator !== 1 && <span className="text-blue-100"> ({item.quantidade} {item.unidade} × {fator} peças)</span>}
              </span>
              <span className="text-lg font-bold">= {formatarMoeda(r.custoUnitario)} por {nomeUnidade}</span>
            </div>

            <BomSaber memoria={r.memoria} />
          </div>
        </details>
      </div>
    </div>
  );
}

/** Resultado do "Salvar e Calcular": custo por unidade de venda explicado para leigo. */
export default function ResultadoCusto({ resultado, itens }) {
  const explicacaoPorIndice = new Map((resultado.explicacao?.itens || []).map(e => [e.indice, e.explicacao]));
  const itemPorIndice = new Map(itens.map(i => [i.indice, i]));
  const regimeLabel = REGIMES.find(r => r.valor === resultado.regime)?.label || resultado.regime;

  return (
    <div className="space-y-4">
      <AvisoSugestaoIA />

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Custo calculado</h3>
            <span className="text-xs text-gray-500">({regimeLabel}{resultado.freteNota > 0 ? ` · frete ${formatarMoeda(resultado.freteNota)}` : ''})</span>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Custo real de toda a nota</p>
            <p className="text-xl font-bold text-gray-900">{formatarMoeda(resultado.custoTotalNota)}</p>
          </div>
        </div>

        {resultado.explicacao?.resumo_geral && (
          <div className="flex items-start gap-2 text-sm text-gray-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 mb-2">
            <Sparkles className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
            <p>{resultado.explicacao.resumo_geral}</p>
          </div>
        )}
        {resultado.avisoIa && (
          <p className="flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="w-3.5 h-3.5" />{resultado.avisoIa}</p>
        )}
      </div>

      {resultado.itens.map(r => (
        <CartaoItem
          key={r.indice}
          r={r}
          item={itemPorIndice.get(r.indice) || {}}
          explicacao={explicacaoPorIndice.get(r.indice)}
        />
      ))}

      <AvisoSugestaoIA compacto />
    </div>
  );
}
